import { Fragment, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { MoneyInput } from '@/components/ui/money-input'
import { useCategories, buildTree } from '@/hooks/useCategories'
import { useBudgets, useSaveBudget, PRESET_MONTH } from '@/hooks/useBudgets'
import { useT } from '@/i18n/I18nProvider'
import { useTranslateCategory } from '@/lib/categoryDisplay'
import { useHousehold } from '@/context/HouseholdContext'
import { formatCurrency } from '@/lib/currency'
import { cn } from '@/lib/utils'
import type { Category } from '@/types/db'

// Editor dos orçamentos PREDEFINIDOS (template, salvos no PRESET_MONTH).
export function PresetDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useT()
  const tc = useTranslateCategory()
  const { baseCurrency } = useHousehold()
  const { data: categories } = useCategories()
  const { data: presets } = useBudgets(PRESET_MONTH)
  const save = useSaveBudget()

  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState(0)

  // Ao reabrir, não reabrir a célula que estava em edição (estado obsoleto).
  useEffect(() => {
    if (open) {
      setEditing(null)
      setDraft(0)
    }
  }, [open])

  const incomeTree = buildTree(categories ?? [], 'income')
  const expenseTree = buildTree(categories ?? [], 'expense')
  const val = (id: string) => presets?.get(id) ?? 0
  const sumKind = (k: 'income' | 'expense') =>
    (categories ?? []).filter((c) => c.kind === k).reduce((s, c) => s + val(c.id), 0)
  const totalIncome = sumKind('income')
  const totalExpense = sumKind('expense')

  function commit(id: string) {
    save.mutate(
      { categoryId: id, month: PRESET_MONTH, amount: draft },
      { onError: (e) => toast.error(t('bud.presetSaveError'), { description: (e as Error).message }) },
    )
    setEditing(null)
  }
  function cell(cat: Category) {
    if (editing === cat.id) {
      return (
        <MoneyInput
          autoFocus
          value={draft}
          onChange={setDraft}
          onBlur={() => commit(cat.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit(cat.id)
            if (e.key === 'Escape') setEditing(null)
          }}
          className="ml-auto h-8 w-28 text-right"
        />
      )
    }
    const v = val(cat.id)
    return (
      <button
        className="hover:underline"
        onClick={() => {
          setDraft(v)
          setEditing(cat.id)
        }}
      >
        {v > 0.005 ? formatCurrency(v, baseCurrency) : <span className="text-muted-foreground">{t('bud.define')}</span>}
      </button>
    )
  }
  function row(cat: Category, indent: boolean) {
    return (
      <TableRow key={cat.id}>
        <TableCell className={cn('font-medium', indent && 'pl-8 font-normal')}>
          <span className="inline-flex items-center gap-1.5">
            {cat.icon && <span>{cat.icon}</span>}
            {tc(cat.name)}
          </span>
        </TableCell>
        <TableCell className="text-right tabular-nums whitespace-nowrap">{cell(cat)}</TableCell>
      </TableRow>
    )
  }
  const renderTree = (tree: ReturnType<typeof buildTree>) =>
    tree.map(({ parent, children }) => (
      <Fragment key={parent.id}>
        <TableRow className="bg-muted/40 hover:bg-muted/40">
          <TableCell className="font-semibold">
            <span className="inline-flex items-center gap-1.5">
              {parent.icon && <span>{parent.icon}</span>}
              {tc(parent.name)}
            </span>
          </TableCell>
          <TableCell className="text-right tabular-nums whitespace-nowrap">{cell(parent)}</TableCell>
        </TableRow>
        {children.map((c) => row(c, true))}
      </Fragment>
    ))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85svh] flex-col overflow-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('bud.presetsTitle')}</DialogTitle>
          <DialogDescription>
            {t('bud.presetsDesc')} {t('bud.income')}{' '}
            <span className="text-success font-medium">{formatCurrency(totalIncome, baseCurrency)}</span> · {t('bud.expenses')}{' '}
            <span className="text-destructive font-medium">{formatCurrency(totalExpense, baseCurrency)}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto pr-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('bud.category')}</TableHead>
                <TableHead className="text-right">{t('bud.presetValue')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {incomeTree.length > 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={2} className="text-success text-xs font-semibold tracking-wide uppercase">
                    {t('bud.income')}
                  </TableCell>
                </TableRow>
              )}
              {renderTree(incomeTree)}
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={2} className="text-destructive text-xs font-semibold tracking-wide uppercase">
                  {t('bud.expenses')}
                </TableCell>
              </TableRow>
              {renderTree(expenseTree)}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  )
}
